import { Button, trackEvent } from "@/components/atoms";
import { SocialLinks } from "@/components/molecules";
import { withImageKitTransform } from "@/utils";
import type { LinkHubLandingConfig, LinkHubLink } from "./types";
import "./LinkHub.scss";

interface LinkHubLandingProps {
  config: LinkHubLandingConfig;
}

const resolveLinkTrackSufix = (
  prefix: string,
  link: LinkHubLink,
): string => {
  if (link.isExternalLink) {
    return `${prefix}_external`;
  }

  return `${prefix}_${link.trackKey ?? "link"}`;
};

/**
 * Shared link-hub landing layout (profile, social links, sections)
 */
export const LinkHub: React.FC<LinkHubLandingProps> = ({ config }) => {
  const { analyticsPrefix } = config;

  const handleSocialClick = (): void => {
    trackEvent("User Interaction", `click_button_${analyticsPrefix}_social`);
  };

  const renderLink = (link: LinkHubLink, index: number) => (
    <Button
      key={index}
      variant={link.isSecondary ? "secondary" : "primary"}
      fullWidth
      density="block"
      borderRadius="lg"
      motionScale="base"
      actionTrackSufix={resolveLinkTrackSufix(analyticsPrefix, link)}
      {...(link.isExternalLink ? { href: link.url } : { to: link.url })}
    >
      {link.title}
    </Button>
  );

  return (
    <>
      <div className="landing__profile">
        <img
          src={withImageKitTransform(config.profile.imageUrl, "linkHub")}
          alt={config.profile.imageAlt}
          className="landing__picture"
          loading="eager"
        />
        <h1 className="landing__title">{config.profile.title}</h1>
        {config.profile.phrase ? (
          <p className="landing__subtitle">{config.profile.phrase}</p>
        ) : null}
        <SocialLinks
          links={config.socialLinks}
          onLinkClick={handleSocialClick}
        />
      </div>
      {config.sections.map((section) => (
        <section
          key={section.id}
          id={section.id}
          className="landing__section"
        >
          <h2 className="landing__section-title">{section.title}</h2>
          <div className="landing__section-container">
            {section.links.map((link, linkIndex) => renderLink(link, linkIndex))}
          </div>
        </section>
      ))}
    </>
  );
};
